import { useContext } from 'react';
import { ApolloCache, InMemoryCache } from '@apollo/client';
import gql from 'graphql-tag';
import { t } from '@lingui/macro';
import { snackbarVar } from '@lib/apollo/policies';
import useLoginModal from '@lib/hooks/useLoginModal';
import { UserContext } from '@lib/contexts/UserProvider';
import {
  CurrentUser,
  useDeleteFavoritePlaylistMutation,
  useAddFavoritePlaylistMutation,
  useAddFavoriteVideoMutation,
  useDeleteFavoriteVideoMutation,
} from '@gql/generated';
import AnalyticsManager from '@lib/analytics/manager';

const GET_CURRENT_USER_FAVORITES = gql`
  query currentUser {
    currentUser {
      id
      favorites {
        playlists
        videos
      }
    }
  }
`;

/**
 * The Logic of these 4's should be refactored
 */
const updateUnfavoriteVideo = (proxy: ApolloCache<InMemoryCache>, videoId) => {
  const data = proxy.readQuery<{ currentUser: CurrentUser }>({
    query: GET_CURRENT_USER_FAVORITES,
  });

  if (data.currentUser) {
    proxy.writeQuery({
      query: GET_CURRENT_USER_FAVORITES,
      data: {
        currentUser: {
          ...data.currentUser,
          favorites: {
            ...data.currentUser.favorites,
            videos: data.currentUser.favorites.videos.filter(
              id => id !== videoId
            ),
          },
        },
      },
    });
  }
};

const updateFavoriteVideo = (proxy: ApolloCache<InMemoryCache>, videoId) => {
  const data = proxy.readQuery<{ currentUser: CurrentUser }>({
    query: GET_CURRENT_USER_FAVORITES,
  });

  if (data.currentUser) {
    proxy.writeQuery({
      query: GET_CURRENT_USER_FAVORITES,
      data: {
        currentUser: {
          ...data.currentUser,
          favorites: {
            ...data.currentUser.favorites,
            videos: [...data.currentUser.favorites.videos, videoId],
          },
        },
      },
    });
  }
};

const updateUnfavoritePlaylist = (
  proxy: ApolloCache<InMemoryCache>,
  playlistId
) => {
  const data = proxy.readQuery<{ currentUser: CurrentUser }>({
    query: GET_CURRENT_USER_FAVORITES,
  });

  if (data.currentUser) {
    proxy.writeQuery({
      query: GET_CURRENT_USER_FAVORITES,
      data: {
        currentUser: {
          ...data.currentUser,
          favorites: {
            ...data.currentUser.favorites,
            playlists: data.currentUser.favorites.playlists.filter(
              id => id !== playlistId
            ),
          },
        },
      },
    });
  }
};

const updateFavoritePlaylist = (
  proxy: ApolloCache<InMemoryCache>,
  playlistId
) => {
  const data = proxy.readQuery<{ currentUser: CurrentUser }>({
    query: GET_CURRENT_USER_FAVORITES,
  });
  if (data.currentUser) {
    proxy.writeQuery({
      query: GET_CURRENT_USER_FAVORITES,
      data: {
        currentUser: {
          ...data.currentUser,
          favorites: {
            ...data.currentUser.favorites,
            playlists: [...data.currentUser.favorites.playlists, playlistId],
          },
        },
      },
    });
  }
};

export const VideoFavoriting = ({ videoId, ...props }) => {
  const [loginModalContent, { openModal }] = useLoginModal({});
  const [
    deleteFavoriteVideo,
    { loading: deleteFavoriteVideoLoading },
  ] = useDeleteFavoriteVideoMutation();
  const [
    addFavoriteVideo,
    { loading: addFavoriteVideoLoading },
  ] = useAddFavoriteVideoMutation();
  const [currentUser, { loading, error }] = useContext(UserContext);

  if (error || loading || currentUser === undefined) {
    return null;
  }

  const handlingFavoritingVideo = async (
    isFavoritedVideoById,
    addFavoriteVideo,
    deleteFavoriteVideo
  ) => {
    if (isFavoritedVideoById) {
      await deleteFavoriteVideo({
        variables: {
          videoId: videoId,
        },
        // TODO: handle failed case
        update: proxy => updateUnfavoriteVideo(proxy, videoId),
      });
    } else {
      AnalyticsManager().sendVideoAddedToFavorites({ video_id: videoId });

      // TODO: handle failed case
      await addFavoriteVideo({
        variables: {
          videoId: videoId,
        },
        // TODO: handle failed case
        update: proxy => {
          updateFavoriteVideo(proxy, videoId);
        },
      });
    }
  };

  const isFavoritedVideoById = currentUser
    ? currentUser?.favorites?.videos?.includes(videoId)
    : false;

  const onClickFavoriteHandler = async () => {
    if (!currentUser) {
      openModal();
    } else {
      await handlingFavoritingVideo(
        isFavoritedVideoById,
        addFavoriteVideo,
        deleteFavoriteVideo
      );
      snackbarVar({
        isSnackBarOpen: true,
        goTo: 'FAVORITES',
        id: 'favorites_2',
        snackBarMessage: isFavoritedVideoById
          ? t`snackbars.unfavorited_video`
          : t`snackbars.favorited_video`,
      });
    }
  };

  return (
    <>
      {loginModalContent}
      {props.children({
        isFavoritedVideoById,
        onClickFavoriteHandler,
        loading: deleteFavoriteVideoLoading || addFavoriteVideoLoading,
      })}
    </>
  );
};

export const PlaylistFavoriting = ({ playlistId, ...props }) => {
  const [loginModalContent, { openModal }] = useLoginModal({});
  const [
    deleteFavoritePlaylist,
    { loading: deleteFavoritePlaylistLoading },
  ] = useDeleteFavoritePlaylistMutation();
  const [
    addFavoritePlaylist,
    { loading: addFavoritePlaylistLoading },
  ] = useAddFavoritePlaylistMutation();
  const [currentUser, { loading, error }] = useContext(UserContext);

  if (error || loading) {
    return null;
  }

  const handlingFavoritingPlaylist = async (
    isPlaylistFavoritedById,
    addFavoritePlaylist,
    deleteFavoritePlaylist
  ) => {
    if (isPlaylistFavoritedById) {
      // PLAYLIST TODO: handle errors
      await deleteFavoritePlaylist({
        variables: {
          playlistId: playlistId,
        },
        // TODO: handle failed case
        update: proxy => {
          updateUnfavoritePlaylist(proxy, playlistId);
        },
      });
    } else {
      try {
        await addFavoritePlaylist({
          variables: {
            playlistId: playlistId,
          },
          // TODO: handle failed case
          update: proxy => {
            updateFavoritePlaylist(proxy, playlistId);
            const data = proxy.readFragment({
              id: 'Playlist:' + playlistId,
              fragment: gql`
                fragment playlist on Playlist {
                  id
                  title
                }
              `,
            });
            AnalyticsManager().sendPlaylistAddedToFavorites({
              title: data?.title,
              playlist_id: playlistId,
            });
          },
        });
      } catch (ex) {
        //
      }
    }
  };

  const isPlaylistFavoritedById = currentUser
    ? currentUser?.favorites?.playlists.includes(playlistId)
    : false;

  const onClickFavoriteHandler = async () => {
    if (!currentUser) {
      // show login modal
      openModal();
    } else {
      await handlingFavoritingPlaylist(
        isPlaylistFavoritedById,
        addFavoritePlaylist,
        deleteFavoritePlaylist
      );

      snackbarVar({
        isSnackBarOpen: true,
        goTo: 'FAVORITES',
        id: 'favorites_1',
        snackBarMessage: isPlaylistFavoritedById
          ? t`snackbars.unfavorited_playlist`
          : t`snackbars.favorited_playlist`,
      });
    }
  };

  return (
    <>
      {loginModalContent}
      {props.children({
        isPlaylistFavoritedById,
        onClickFavoriteHandler,
        loading: deleteFavoritePlaylistLoading || addFavoritePlaylistLoading,
      })}
    </>
  );
};
